import { z } from 'zod';
import type { ComponentType } from 'react';
import type { IconProps } from '@shared/icons';
import { fromBackendRole } from '../lib/constants';

type IconComponent = ComponentType<IconProps>;

/* ================================================================== */
/*  Domain types — canonical definitions for the team feature slice    */
/* ================================================================== */

/* ─────────────────────────── Scope ──────────────────────────────── */

export type Scope =
  | { kind: 'org' }
  | {
      kind: 'scoped';
      mcps?: string[];
      agents?: string[];
      policies?: string[];
      tools?: string[];
    };

/* ─────────────────────────── Members ────────────────────────────── */

export type TeamRole = 'org_admin' | 'iam_admin' | 'developer' | 'analyst' | 'viewer';

export type MemberStatus = 'active' | 'pending' | 'suspended';

/** Derived "freshness" bucket for the last sign-in (drives a Phase B filter). */
export type LastSignInBucket = '7d' | '30d' | '90d' | 'never' | null;

export type Member = {
  /** Membership id (`membershipId`) — the stable row key used by the UI. */
  id: string;
  /**
   * The member's user id. Backend member-mutation routes
   * (`/organization/:orgId/members/:userId/...`) key on this, NOT the
   * membership id. Optional because demo/fixture members carry no real
   * user id; callers fall back to `id` in that case.
   */
  userId?: string;
  name: string;
  email: string;
  /** Role key — a built-in `TeamRole` or a custom role key. */
  role: string;
  status: MemberStatus;
  /** Display string (e.g. "12m ago", "Yesterday", "Jul 14"). */
  lastActive: string;
  /** Reference to a FunctionalTeam, or null. */
  teamId: string | null;
  joined: string;
  /** Dashboard visibility + edit scope. Defaults to whole-org. */
  scope: Scope;
  /** Optional title shown in the detail header. */
  title?: string;
  /** Derived sign-in freshness bucket. */
  lastSignInBucket?: LastSignInBucket;
};

/* ─────────────────────────── Teams ──────────────────────────────── */

export type FunctionalTeam = {
  id: string;
  name: string;
  ownerId: string | null;
  defaultScope?: Scope;
};

/* ─────────────────────────── Roles (capability grid) ────────────── */

type PermissionKey =
  | 'view-inventory'
  | 'edit-agents'
  | 'add-mcp'
  | 'manage-policies'
  | 'approve-intent'
  | 'view-audit'
  | 'manage-keys'
  | 'invite-members'
  | 'assign-roles'
  | 'billing';

export type PermissionRow = {
  key: PermissionKey;
  label: string;
  description: string;
};

export type RoleDef = {
  key: TeamRole;
  label: string;
  description: string;
  members: number;
  permissions: Readonly<Record<PermissionKey, boolean>>;
};

/* ─────────────────────────── RBAC model ─────────────────────────── */

export type Level = 'none' | 'view' | 'full';

export type LevelMeta = {
  label: string;
  short: string;
  fgClass: string;
  bgClass: string;
  borderClass: string;
  rank: number;
};

export type DomainKey =
  | 'userManagement'
  | 'vulnerabilityScanning'
  | 'mcpServerManagement'
  | 'agentServerManagement'
  | 'auditLog';

export type Domain = {
  key: DomainKey;
  label: string;
  icon: IconComponent;
  blurb: string;
};

export type RbacRole = {
  key: string;
  name: string;
  icon: IconComponent;
  system: boolean;
  desc: string;
  matrix: Record<DomainKey, Level>;
};

/** Lightweight member shape used by the RBAC roles slice (distinct from the
 *  full `Member` type used by the members table). */
export type RbacMember = {
  name: string;
  role: string;
};

/* ─────────────────────── Scope resources ────────────────────────── */

export type ScopeResourceType = 'mcps' | 'agents' | 'policies' | 'tools';

export type ScopeResource = {
  type: ScopeResourceType;
  id: string;
  label: string;
  sublabel?: string;
  iconSlug?: string;
};

/* ─────────────────────────── Access ─────────────────────────────── */

export type AccessLevel = 'view' | 'full';

export type ResolvedAccessResource = ScopeResource & {
  dangling: boolean;
  level: AccessLevel;
};

export type EffectiveAccess = {
  wholeOrg: boolean;
  byType: Record<ScopeResourceType, ResolvedAccessResource[]>;
  danglingCount: number;
};

/* ─────────────────────── Member views ───────────────────────────── */

export type GroupBy = 'none' | 'team' | 'role' | 'status' | 'lastSignIn';

export type MemberFilters = {
  status: ReadonlyArray<MemberStatus>;
  role: ReadonlyArray<string>;
  team: ReadonlyArray<string>;
  seen: ReadonlyArray<Exclude<LastSignInBucket, null>>;
  scopedOnly: boolean;
};

export type MemberGroup = { key: string; label: string; members: ReadonlyArray<Member> };

type OwnedAgent = { id: string; name: string; env: string };
type OwnedAutomation = { id: string; name: string; cadence: string };
type AuthoredPolicy = { id: string; name: string; status: string };
type RunningPlan = { id: string; title: string; startedAgo: string };

export type MemberRelationships = {
  agents: ReadonlyArray<OwnedAgent>;
  automations: ReadonlyArray<OwnedAutomation>;
  policies: ReadonlyArray<AuthoredPolicy>;
  plans: ReadonlyArray<RunningPlan>;
};

export type ActivityLens = 'govern' | 'spend' | 'perform';

export type ActivityRow = {
  id: string;
  lens: ActivityLens;
  action: string;
  detail: string;
  severity: 'info' | 'warn' | 'bad';
  when: string;
  ref?: { kind: 'plan' | 'decision' | 'agent'; label: string };
};

/* ─────────────────────── Pickable roles ─────────────────────────── */

export type PermissionState = 'full' | 'view' | 'none';

type PickablePermission = {
  label: string;
  state: PermissionState;
};

export type PickableRole = {
  key: string;
  label: string;
  description: string;
  system: boolean;
  permissions: ReadonlyArray<PickablePermission>;
};

/* ================================================================== */
/*  Backend schemas (Zod) + mappers                                    */
/* ================================================================== */

export const BackendMemberSchema = z.object({
  membershipId: z.string(),
  orgId: z.string(),
  userId: z.string(),
  role: z.string(),
  teamId: z.string().nullable().optional(),
  scope: z.unknown().nullable().optional(),
  title: z.string().nullable().optional(),
  memberStatus: z.string().optional(),
  lastSignInAt: z.string().nullable().optional(),
  activatedAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  user: z.object({
    userId: z.string(),
    email: z.string(),
    displayName: z.string().nullable().optional(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
  }),
});

export type BackendMember = z.infer<typeof BackendMemberSchema>;

/** Map backend memberStatus to frontend MemberStatus. */
function deriveMemberStatus(raw: BackendMember): MemberStatus {
  const s = raw.memberStatus?.toLowerCase();
  if (s === 'suspended') return 'suspended';
  if (s === 'invited' || s === 'pending') return 'pending';
  if (s === 'active') return 'active';
  // Fallback: derive from activatedAt if memberStatus is absent
  return raw.activatedAt ? 'active' : 'pending';
}

/** Parse the scope JSON blob into a typed Scope. Default to org-wide. */
function parseScope(raw: unknown): Scope {
  if (raw == null || typeof raw !== 'object') return { kind: 'org' };
  const obj = raw as Record<string, unknown>;
  if (obj.kind === 'scoped') return raw as Scope;
  if (obj.kind === 'org') return { kind: 'org' };
  return { kind: 'org' };
}

/** Compute a human-readable relative time string from an ISO timestamp. */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '--';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '--';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'Just now';

  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;

  // Fall back to short date
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Derive a sign-in freshness bucket from a last-sign-in ISO timestamp. */
function signInBucket(iso: string | null | undefined): LastSignInBucket {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never';
  const days = (Date.now() - then) / 86_400_000;
  if (days <= 7) return '7d';
  if (days <= 30) return '30d';
  if (days <= 90) return '90d';
  return 'never';
}

export function toMember(raw: BackendMember): Member {
  const name =
    raw.user.displayName ||
    [raw.user.firstName, raw.user.lastName].filter(Boolean).join(' ') ||
    raw.user.email.split('@')[0];

  const status: MemberStatus = deriveMemberStatus(raw);
  const scope: Scope = parseScope(raw.scope);

  return {
    id: raw.membershipId,
    userId: raw.userId,
    name,
    email: raw.user.email,
    role: fromBackendRole(raw.role),
    status,
    lastActive: relativeTime(raw.lastSignInAt),
    teamId: raw.teamId ?? null,
    joined: raw.createdAt ?? '--',
    scope,
    title: raw.title ?? undefined,
    lastSignInBucket: signInBucket(raw.lastSignInAt),
  };
}

export const MemberListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(BackendMemberSchema),
});

/* ------------------------------------------------------------------ */
/*  Invite schemas                                                     */
/* ------------------------------------------------------------------ */

const BackendInviteSchema = z.object({
  inviteId: z.string(),
  email: z.string(),
  role: z.string(),
  teamId: z.string().nullable().optional(),
  scope: z.unknown().nullable().optional(),
  status: z.string(),
  invitedBy: z.string().nullable().optional(),
  expiresAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
});

export type BackendInvite = z.infer<typeof BackendInviteSchema>;

export const InviteListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(BackendInviteSchema),
});

/** Map backend invite status to the frontend union. */
function deriveInviteStatus(raw: string): InviteStatus {
  const s = raw.toLowerCase();
  if (s === 'accepted') return 'accepted';
  if (s === 'expired') return 'expired';
  if (s === 'revoked') return 'revoked';
  return 'pending';
}

/** Compute a human-readable "expires in" string from an ISO expiry date. */
function expiresInLabel(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return undefined;
  const diffMs = then - Date.now();
  if (diffMs <= 0) return 'expired';
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 24) return `${hours} hours`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export type Invite = {
  id: string;
  email: string;
  role: string;
  sent: string;
  invitedBy: string;
  status: InviteStatus;
  expiresIn?: string;
  teamId: string | null;
  scope: Scope;
};

export function toInvite(raw: BackendInvite): Invite {
  return {
    id: raw.inviteId,
    email: raw.email,
    role: fromBackendRole(raw.role),
    sent: raw.createdAt
      ? new Date(raw.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '--',
    invitedBy: raw.invitedBy ?? 'System',
    status: deriveInviteStatus(raw.status),
    expiresIn: expiresInLabel(raw.expiresAt),
    teamId: raw.teamId ?? null,
    scope: parseScope(raw.scope),
  };
}

/* ------------------------------------------------------------------ */
/*  Invite-token lookup (public, unauthenticated)                      */
/* ------------------------------------------------------------------ */

/**
 * Zod schema for `GET /organization/invitations/:token` — the public
 * endpoint that returns invite details for the accept-invite page.
 *
 * Backend throws 404 (not found / already used) or 400 (expired).
 */
export const InviteTokenResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    invitationId: z.string(),
    email: z.string(),
    role: z.string(),
    organization: z.object({
      orgId: z.string(),
      name: z.string(),
      domain: z.string().nullable().optional(),
      vertical: z.string().nullable().optional(),
    }),
    invitedBy: z.string(),
    sentAt: z.string(),
    expiresAt: z.string(),
  }),
});

export type InviteTokenResponse = z.infer<typeof InviteTokenResponseSchema>;

export const AcceptInviteResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    organization: z.object({
      orgId: z.string().min(1),
    }),
  }),
});

export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponseSchema>;

/** The payload shape consumed by AcceptInviteRoute. */
export type InvitePayload = {
  orgId: string;
  orgName: string;
  orgAvatar: { initials: string; color: 'accent' };
  inviterName: string;
  inviterEmail: string;
  roleKey: string;
  email: string;
};

/** Derive org-avatar initials from the org name (up to 2 chars). */
function orgInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Map the backend invite-token response to the UI payload. */
export function toInvitePayload(data: InviteTokenResponse['data']): InvitePayload {
  return {
    orgId: data.organization.orgId,
    orgName: data.organization.name,
    orgAvatar: { initials: orgInitials(data.organization.name), color: 'accent' },
    inviterName: data.invitedBy,
    // The backend does not return the inviter's email — use a display-only fallback.
    inviterEmail: '',
    roleKey: fromBackendRole(data.role),
    email: data.email,
  };
}

/* ------------------------------------------------------------------ */
/*  Join request schemas                                               */
/* ------------------------------------------------------------------ */

/**
 * Backend `GET /organization/:orgId/requests` nests the requester under `user`
 * and wraps the list in `data.requests` (alongside `organization` + `summary`).
 * Join requests carry no requested role — a role is assigned when an admin
 * approves the request.
 */
const BackendJoinRequestUserSchema = z.object({
  userId: z.string(),
  email: z.string(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  fullName: z.string().nullable().optional(),
});

export const BackendJoinRequestSchema = z.object({
  requestId: z.string(),
  status: z.string(),
  createdAt: z.string().optional(),
  user: BackendJoinRequestUserSchema,
});

export type BackendJoinRequest = z.infer<typeof BackendJoinRequestSchema>;

export const JoinRequestListResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    requests: z.array(BackendJoinRequestSchema),
  }),
});

export type JoinRequest = {
  id: string;
  email: string;
  roleKey: string;
  teamId: string | null;
  requestedAgo: string;
  sourceLink: string;
};

export function toJoinRequest(raw: BackendJoinRequest): JoinRequest {
  return {
    id: raw.requestId,
    email: raw.user.email,
    // Backend join requests carry no requested role; it is set on approval.
    roleKey: '',
    teamId: null,
    requestedAgo: raw.createdAt ? relativeTime(raw.createdAt) : '--',
    sourceLink: '',
  };
}
